import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { TokenUsageDataResult } from '../types';
import { useTokenUsageData } from './useTokenUsageData';

/**
 * Custom hook to fetch FILTERED token usage data for chart display
 *
 * This hook fetches token usage data with filters applied for the CURRENT date range
 * and grouping only. Unlike useMultiDimensionalTokenPrefetch, it does NOT:
 * - Prefetch multiple groupings
 * - Use staged loading
 *
 * It's a simple, single query for displaying filtered chart data.
 * Filter options should come from unfiltered data (useMultiDimensionalTokenPrefetch).
 *
 * @param selectedModels Applied model filters
 * @param selectedWorkspaces Applied workspace filters
 * @param selectedUsers Applied user filters
 * @param currentTimeRange Current date range parameters
 * @param groupBy Current grouping selection
 * @returns Object containing filtered token usage data, loading state, error, and refetch function
 */
export function useFilteredTokenUsageData(
  selectedModels: string[],
  selectedWorkspaces: string[],
  selectedUsers: string[],
  currentTimeRange: {
    startTime: Date;
    endTime: Date;
    granularity: Granularity;
  },
  groupBy: H2OGPTeGrouping
): TokenUsageDataResult {
  const { startTime, endTime, granularity } = currentTimeRange;

  // Single query for the current date range + grouping + filters
  const { usageData, isLoading, error, refetch } = useTokenUsageData({
    startTime,
    endTime,
    granularity,
    groupBy,
    selectedModels,
    selectedWorkspaces,
    selectedUsers,
  });

  return {
    usageData,
    // No workspace/user data - filter options come from unfiltered data
    workspaceData: undefined,
    userData: undefined,
    isLoading,
    error,
    refetch,
  };
}
